import { Typography } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { PrimaryButton } from '@components/atoms/PrimaryButton/PrimaryButton';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { services, Service, getServiceById } from '@common/data/services';
import 'swiper/css';
import 'swiper/css/navigation';
import './ServicesPage.css';

const { Title, Paragraph } = Typography;

export const ServicesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A service can be deep-linked with /services?service=<id> (used by the home page cards)
  const [selectedService, setSelectedService] = useState<Service>(
    getServiceById(searchParams.get('service') || '') ||
      getServiceById('excavation') ||
      services[0]
  ); // Default to Excavation
  const swiperRef = useRef<SwiperType | null>(null);
  const serviceDetailsRef = useRef<HTMLElement | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const handleServiceClick = (service: Service) => {
    setSelectedService(service);
    // Scroll to service details section
    if (serviceDetailsRef.current) {
      serviceDetailsRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };
  
  const handlePrevClick = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
      
      // On mobile, update the selected service to the active slide
      if (isMobile && swiperRef.current.activeIndex !== undefined) {
        // Need to account for loop mode
        const realIndex = swiperRef.current.realIndex;
        const service = services[realIndex];
        if (service) {
          setSelectedService(service);
        }
      }
    }
  };
  
  const handleNextClick = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
      
      // On mobile, update the selected service to the active slide
      if (isMobile && swiperRef.current.activeIndex !== undefined) {
        // Need to account for loop mode
        const realIndex = swiperRef.current.realIndex;
        const service = services[realIndex];
        if (service) {
          setSelectedService(service);
        }
      }
    }
  };
  
  // Listen for slide changes
  const handleSlideChange = (swiper: SwiperType) => {
    if (isMobile) {
      const realIndex = swiper.realIndex;
      const service = services[realIndex];
      if (service) {
        setSelectedService(service);
      }
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full h-[400px]">
        <div className="services-bg opacity-60 absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <Title level={1} className="font-android !font-normal text-yellow-principal text-center">
            OUR SERVICES
          </Title>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
              Comprehensive excavation and construction solutions for projects of all sizes.
            </Paragraph>
          </div>
        </div>
      </section>
      
      {/* Main Services */}
      <section className="w-full pt-8 pb-18 bg-second-black-carbon">
        <div className="container mx-auto relative z-10">
          <Title level={2} className="font-android !font-normal text-yellow-principal text-center text-2xl md:text-3xl lg:text-4xl mb-4 mt-4">
            WHAT WE OFFER
          </Title>          
          <Paragraph className="font-barlow text-gray-300 text-center max-w-xl mx-auto md:pb-4">
          At Ruppert Inc., we provide a wide range of excavation and construction services to meet the needs of residential, commercial, and industrial clients throughout the region.
          </Paragraph>
          
          <div className="mx-4 md:mx-auto relative">
            <div className="services-carousel-container relative">
              <Swiper
                modules={[Navigation, Keyboard]}
                spaceBetween={32}
                slidesPerView={1}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                onSlideChange={handleSlideChange}
                keyboard={{
                  enabled: true,
                }}
                loop={true}
                breakpoints={{
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 48,
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 48,
                  },
                }}
                className="services-carousel !pt-8"
              >
                {services.map((service) => (
                  <SwiperSlide key={service.id}>
                    <div 
                      className={`flex flex-col p-6 h-full cursor-pointer transition-all duration-300 ${
                        selectedService.id === service.id 
                          ? 'bg-yellow-principal text-black' 
                          : 'bg-first-dark-gray hover:bg-zinc-700'
                      }`}
                      onClick={() => handleServiceClick(service)}
                    >
                      <Title 
                        level={4} 
                        className={`font-android !font-normal text-lg md:text-xl ${
                          selectedService.id === service.id ? '!text-black' : '!text-yellow-principal'
                        }`}
                      >
                        {service.title}
                      </Title>
                      <Paragraph 
                        className={`font-barlow text-xs md:text-sm mb-3 ${
                          selectedService.id === service.id ? 'text-black' : 'text-gray-300'
                        }`}
                      >
                        {service.shortDescription}
                      </Paragraph>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              
              {/* Custom navigation buttons */}
              <button 
                className="custom-prev-button absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-first-dark-gray border-2 border-transparent hover:border-yellow-principal rounded-full"
                onClick={handlePrevClick}
              >
                <svg className="w-6 h-6 text-yellow-principal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button 
                className="custom-next-button absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-first-dark-gray border-2 border-transparent hover:border-yellow-principal rounded-full"
                onClick={handleNextClick}
              >
                <svg className="w-6 h-6 text-yellow-principal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Service Details Section */}
      <section ref={serviceDetailsRef} className="w-full h-full bg-first-dark-gray pb-8 md:pb-12">
        <div className="container mx-auto px-4">          
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="md:w-1/2 pt-4 md:pt-6">
              <div className="mt-3">
                <Title level={2} className="font-android !font-normal text-yellow-principal pb-4 md:pb-6 text-2xl md:text-3xl lg:text-4xl">
                  {selectedService.title}
                </Title>
                <Paragraph className="font-barlow text-gray-300 text-sm md:text-base text-justify pb-4">
                  {selectedService.description}
                </Paragraph>
                
                <div className="text-gray-300 mb-6">
                  {selectedService.features.map((feature, index) => (
                    <div key={index} className="flex items-start mb-4">
                      <span className="mr-2">
                        <img src="/assets/icons/carry-right.svg" alt="bullet" className="w-5 h-5 pt-[2px]" />
                      </span>
                      <span className="font-barlow text-sm md:text-base">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-center md:justify-start my-8 md:my-12">
                  <PrimaryButton 
                    size="large" 
                    onClick={() => { navigate('/quote'); window.scrollTo(0, 0); }}
                    className="w-auto"
                  >
                  REQUEST A QUOTE
                  </PrimaryButton>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2 flex items-center justify-center">
              <div className="overflow-hidden">
                <img 
                  src={selectedService.image} 
                  alt={selectedService.title} 
                  className="w-full h-auto max-h-[800px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Equipment Section */}
      <section className="w-full pt-8 pb-16 bg-second-black-carbon">
        <div className="container mx-auto px-4">
          <Title level={2} className="font-android !font-normal text-yellow-principal text-center text-2xl md:text-3xl lg:text-4xl mb-4 mt-4">
            OUR EQUIPMENT
          </Title>
          <Paragraph className="font-barlow text-gray-300 text-center max-w-3xl mx-auto md:pb-12">
            We maintain a modern fleet of well-maintained equipment to handle projects of all sizes efficiently and safely.
          </Paragraph>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Card 1: Excavators */}
            <div className="flex flex-col bg-first-dark-gray p-6">
              <Title level={4} className="font-android !font-normal !text-white text-lg md:text-xl mb-2">EXCAVATORS</Title>
              <Paragraph className="font-barlow text-gray-300 text-xs md:text-sm">
                Various sized excavators for different project requirements, from compact models for tight spaces to larger machines for major earthmoving.
              </Paragraph>
            </div>

            {/* Card 2: Bulldozers */}
            <div className="flex flex-col bg-first-dark-gray p-6">
              <Title level={4} className="font-android !font-normal !text-white text-lg md:text-xl mb-2">BULLDOZERS</Title>
              <Paragraph className="font-barlow text-gray-300 text-xs md:text-sm">
                Powerful bulldozers for efficient earth moving, grading, and site preparation on projects of all sizes.
              </Paragraph>
            </div>
            
            {/* Card 3: Backhoes */}
            <div className="flex flex-col bg-first-dark-gray p-6">
              <Title level={4} className="font-android !font-normal !text-white text-lg md:text-xl mb-2">BACKHOES</Title>
              <Paragraph className="font-barlow text-gray-300 text-xs md:text-sm">
                Versatile backhoe loaders that combine the digging capabilities of an excavator with the loading capabilities of a front loader.
              </Paragraph>
            </div>

            {/* Card 4: Dump Trucks */}
            <div className="flex flex-col bg-first-dark-gray p-6">
              <Title level={4} className="font-android !font-normal !text-white text-lg md:text-xl mb-2">DUMP TRUCKS</Title>
              <Paragraph className="font-barlow text-gray-300 text-xs md:text-sm">
                Fleet of dump trucks for efficient material hauling, debris removal, and site cleanup.
              </Paragraph>
            </div>

            {/* Card 5: Skid Steers */}
            <div className="flex flex-col bg-first-dark-gray p-6">
              <Title level={4} className="font-android !font-normal !text-white text-lg md:text-xl mb-2">SKID STEERS</Title>
              <Paragraph className="font-barlow text-gray-300 text-xs md:text-sm">
                Compact, highly maneuverable skid steer loaders for working in confined spaces and handling various attachments.
              </Paragraph>
            </div>

            {/* Card 6: Snow Removal Equipment */}
            <div className="flex flex-col bg-first-dark-gray p-6">
              <Title level={4} className="font-android !font-normal !text-white text-lg md:text-xl mb-2">SNOW REMOVAL EQUIPMENT</Title>
              <Paragraph className="font-barlow text-gray-300 text-xs md:text-sm">
                Specialized equipment for commercial snow removal, including plows, snow blowers, and salt/sand spreaders.
              </Paragraph>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="w-full pt-8 pb-16 bg-first-dark-gray">
        <div className="container mx-auto px-4 text-center mt-4">
          <Title level={2} className="font-android !font-normal text-yellow-principal">NEED OUR SERVICES?</Title>
          <Paragraph className="font-barlow text-gray-300 text-xl py-4">
            Contact us today to discuss your project requirements and get a detailed quote from our team.
          </Paragraph>
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            <PrimaryButton
              size="large" 
              onClick={() => { navigate('/quote'); window.scrollTo(0, 0); }}
              className="w-auto">
              GET A QUOTE
          </PrimaryButton>
          {/* <PrimaryButton
              size="large" 
              onClick={() => navigate('/projects')}
              className="w-auto">
              VIEW OUR PROJECTS
            </PrimaryButton> */}
          </div>
        </div>
      </section>

      {/* Quote process
      <section className="w-full py-20 bg-black">
        <div className="container mx-auto px-4">
          <Title level={2} className="!text-white text-center mb-16 font-android">OUR PROCESS</Title>
          
          <div className="relative max-w-4xl mx-auto">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-yellow-principal -ml-0.5"></div>
            
            {/* Step 1 
            <div className="relative mb-16">
              <div className="md:flex items-center">
                <div className="hidden md:block w-1/2 pr-8 text-right">
                  <Title level={3} className="!text-white mb-4 font-android">INITIAL CONSULTATION</Title>
                  <Paragraph className="text-gray-300" style={paragraphStyle}>
                    We begin with a thorough consultation to understand your project needs, goals, and specifications.
                  </Paragraph>
                </div>
                <div className="md:absolute md:left-1/2 md:top-1/2 md:-translate-y-1/2 md:-ml-6 z-10 mb-4 md:mb-0">
                  <div className="bg-yellow-principal text-black w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">1</div>
                </div>
                <div className="md:w-1/2 md:pl-8 md:text-left">
                  <Title level={3} className="!text-white mb-4 md:hidden font-android">INITIAL CONSULTATION</Title>
                  <Paragraph className="text-gray-300 md:hidden" style={paragraphStyle}>
                    We begin with a thorough consultation to understand your project needs, goals, and specifications.
                  </Paragraph>
                  <img 
                    src="/service-consultation.jpg" 
                    alt="Initial Consultation" 
                    className="rounded-lg w-full"
                    onError={(e) => {
                      e.currentTarget.src = 'https://placehold.co/600x400?text=Consultation';
                    }}
                  />
                </div>
              </div>
	          {/* Step 2 
            <div className="relative mb-16">
              <div className="md:flex items-center">
                <div className="md:w-1/2 md:pr-8 md:text-right">
                  <img 
                    src="/service-planning.jpg" 
                    alt="Project Planning" 
                    className="rounded-lg w-full"
                    onError={(e) => {
                      e.currentTarget.src = 'https://placehold.co/600x400?text=Planning';
                    }}
                  />
                </div>
                <div className="md:absolute md:left-1/2 md:top-1/2 md:-translate-y-1/2 md:-ml-6 z-10 mb-4 md:mb-0">
                  <div className="bg-yellow-principal text-black w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">2</div>
                </div>
                <div className="md:w-1/2 md:pl-8 md:text-left">
                  <Title level={3} className="!text-white mb-4 font-android">PROJECT PLANNING</Title>
                  <Paragraph className="text-gray-300" style={paragraphStyle}>
                    Our team develops a detailed project plan, including timeline, resource allocation, and budget considerations.
                  </Paragraph>
                </div>
              </div>
	          {/* Step 3 
            <div className="relative">
              <div className="md:flex items-center">
                <div className="hidden md:block w-1/2 pr-8 text-right">
                  <Title level={3} className="!text-white mb-4 font-android">EXECUTION & COMPLETION</Title>
                  <Paragraph className="text-gray-300" style={paragraphStyle}>
                    We execute the project with precision and attention to detail, keeping you informed throughout the process.
                  </Paragraph>
                </div>
                <div className="md:absolute md:left-1/2 md:top-1/2 md:-translate-y-1/2 md:-ml-6 z-10 mb-4 md:mb-0">
                  <div className="bg-yellow-principal text-black w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">3</div>
                </div>
                <div className="md:w-1/2 md:pl-8 md:text-left">
                  <Title level={3} className="!text-white mb-4 md:hidden font-android">EXECUTION & COMPLETION</Title>
                  <Paragraph className="text-gray-300 md:hidden" style={paragraphStyle}>
                    We execute the project with precision and attention to detail, keeping you informed throughout the process.
                  </Paragraph>
                  <img 
                    src="/service-execution.jpg" 
                    alt="Execution & Completion" 
                    className="rounded-lg w-full"
                    onError={(e) => {
                      e.currentTarget.src = 'https://placehold.co/600x400?text=Execution';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>*/}
    </div>
  );
}; 